import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useHover from './useHover';
import css from './styles/smartTooltip.scss';

interface SmartTooltipProps {
  title: string;
  target: MutableRefObject<Element>;
}

const SmartTooltip: React.FC<SmartTooltipProps> = ({ title, target }) => {
  const currentTarget = target?.current;
  if (!currentTarget) return null;

  const [offsetTop, setOffsetTop] = useState(0);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const isHovered = useHover(target);
  const viewportOffset = currentTarget.getBoundingClientRect();

  const getWidth = (): number => window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;
  const getHeight = (): number => window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight;

  const [windowWidth, setWindowWidth] = useState<number>(getWidth());
  const [windowHeight, setWindowHeight] = useState<number>(getHeight());

  // Window resize needs to trigger a recalculation of offsets
  useEffect(() => {
    let timeoutId = null;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(getWidth());
        setWindowHeight(getHeight());
      }, 150);
    };

    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  useEffect(() => {
    setOffsetTop(viewportOffset.top);
    setOffsetLeft(viewportOffset.left);
  }, [viewportOffset.top, viewportOffset.left, windowWidth, windowHeight]);

  const tooltipRef = useRef(null);
  const currentTooltip = tooltipRef?.current;
  const tooltip = (
    <div
      ref={tooltipRef}
      className={css.smartTooltip}
      style={{
        top: offsetTop + (currentTarget.clientHeight + 5),
        left: offsetLeft -
            (currentTooltip ? currentTooltip.clientWidth / 2 : 0) +
            (currentTarget.clientWidth / 2),
        opacity: isHovered ? 1 : 0,
        visibility: isHovered ? 'visible' : 'hidden',
      }}>
      {title}
    </div>
  );

  return createPortal(tooltip, document.body);
};

export default SmartTooltip;
